import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import calendarGreyIcon from 'assets/images/icons/calendar-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import { Calendar } from 'react-multi-date-picker';
import TimeSpinnerInput from './TimeSpinnerInput';
import DropdownWrapper from './DropdownWrapper';

const DateTimePickerDropdown = ({
  name,
  value,
  onChange,
  hideTime = false,
  format12Hour,
  defaultTime,
  mountTarget,
  align,
  zIndex,
}) => {
  const [timeString, setTimeString] = useState('');

  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : new Date());

  const timeRef = useRef();

  function displayLocalTime() {
    if (!value) {
      return !hideTime ? 'mm/dd/yyyy - 00:00:00' : 'mm/dd/yyyy';
    }
    const d = new Date(value);
    return d
      .toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        ...(!hideTime && { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
      })
      .replace(',', ' -');
  }

  const handleDateChange = (date) => {
    setSelectedDate(date.toDate());
  };

  useEffect(() => {
    if (!value) return;
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      setTimeString(date.toISOString());
    }
  }, [value, format12Hour]);

  const handleCompleteTime = () => {
    const completeTime = new Date(selectedDate);

    if (!hideTime) {
      const isoTimeString = timeRef.current?.getTime();
      if (!isoTimeString) return;
      const time = new Date(isoTimeString);
      completeTime.setHours(time.getHours());
      completeTime.setMinutes(time.getMinutes());
      completeTime.setSeconds(0);
      completeTime.setMilliseconds(0);
    } else {
      if (defaultTime === 'startOfDay') {
        completeTime.setHours(0);
        completeTime.setMinutes(0);
        completeTime.setSeconds(0);
        completeTime.setMilliseconds(0);
      } else if (defaultTime === 'endOfDay') {
        completeTime.setHours(23);
        completeTime.setMinutes(59);
        completeTime.setSeconds(59);
        completeTime.setMilliseconds(999);
      } else {
        const time = new Date();
        completeTime.setHours(time.getHours());
        completeTime.setMinutes(time.getMinutes());
        completeTime.setSeconds(0);
        completeTime.setMilliseconds(0);
      }
    }
    onChange(name, completeTime.toISOString());
  };

  return (
    <DropdownWrapper
      classes='border-0'
      toggleClasses='justify-content-between px-2 py-2 d-flex gap-2 align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
      zIndex={zIndex}
      toggleComponent={
        <>
          <div className='d-flex gap-2'>
            <img style={{ height: '20px' }} src={calendarGreyIcon} />
            <span>{displayLocalTime()}</span>
          </div>
          <img src={chevronGreyIcon} className='rotate-180' />
        </>
      }
      onToggle={(open) => {
        if (open && value) {
          const date = new Date(value);

          setSelectedDate(date);
        }
      }}
      dropdownContent={({ handleCloseDropdown }) => (
        <div className='width-max-content theme-background-white-1 border-grey-13-h-1 rounded-sm'>
          <Calendar
            className='all-elements-color-unset green'
            style={{ boxShadow: 'none' }}
            value={selectedDate ? new Date(selectedDate) : new Date()}
            onChange={handleDateChange}
            format={format12Hour ? 'MM/DD/YYYY hh:mm:ss A' : 'MM/DD/YYYY HH:mm:ss'}
          />

          {!hideTime && (
            <div className='d-flex justify-content-between align-items-center px-3'>
              <span className='theme-size-1_3'>Time</span>
              <TimeSpinnerInput
                ref={timeRef}
                value={timeString}
                format12Hour={format12Hour}
                defaultTime={defaultTime}
              />
            </div>
          )}

          <div className='m-3 border-top-grey-10-h-1'></div>

          <div className='pb-3 mx-3 d-flex justify-content-end gap-3'>
            <div
              className='theme-button-white-1 px-2 py-1 rounded-sm cursor-pointer'
              onClick={handleCloseDropdown}
            >
              Close
            </div>
            <div
              className='theme-button-green-1 px-2 py-1 rounded-sm cursor-pointer'
              onClick={() => {
                handleCompleteTime();
                handleCloseDropdown();
              }}
            >
              Done
            </div>
          </div>
        </div>
      )}
      mountTarget={mountTarget}
      align={align}
      // fullWidth
    />
  );
};

DateTimePickerDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  hideTime: PropTypes.bool,
  format12Hour: PropTypes.bool,
  defaultTime: PropTypes.string,
  mountTarget: PropTypes.string,
  align: PropTypes.string,
  zIndex: PropTypes.number,
};

export default DateTimePickerDropdown;
